import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ShoppingBag, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/orders/my`);
      setOrders(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const formatPrice = (price, currency = 'USD') => new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(price);
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const statusStyle = {
    pending: 'bg-amber-900/30 text-amber-300 border-amber-700',
    confirmed: 'bg-emerald-900/30 text-emerald-300 border-emerald-700',
    shipped: 'bg-blue-900/30 text-blue-300 border-blue-700',
    delivered: 'bg-emerald-900/30 text-emerald-300 border-emerald-700',
    cancelled: 'bg-red-900/30 text-red-300 border-red-700',
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen py-8" data-testid="orders-page">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
        <h1 className="font-display text-3xl text-foreground font-semibold mb-2">My Orders</h1>
        <p className="text-muted-foreground text-sm mb-8">Track your watch purchases</p>

        {orders.length === 0 ? (
          <Card className="p-12 border-border bg-card text-center" data-testid="orders-empty">
            <ShoppingBag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-foreground mb-2">No orders yet</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              You haven't purchased any watches yet. Browse our curated collection of authenticated luxury timepieces.
            </p>
            <Button onClick={() => navigate('/catalog')} data-testid="orders-browse-button">
              Browse Catalog
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map(order => (
              <Card key={order.id} className="p-5 border-border bg-card" data-testid={`order-${order.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Order {order.orderNumber || `#${order.id.slice(-8).toUpperCase()}`}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{formatDate(order.purchasedAt || order.createdAt)}</p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] capitalize ${statusStyle[order.status] || ''}`}>
                    {order.status}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">Watch Order</p>
                  <p className="text-primary font-semibold">{formatPrice(order.totalPrice, order.currency)}</p>
                </div>
                {order.shippingAddress && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Ship to: {order.shippingAddress.city}, {order.shippingAddress.country}
                  </p>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
